import { Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';
import { GmailAuditModel, GmailExportKey } from '../../../../types';
import CreateButton from '../../../shared/buttons/create-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Key from '@mui/icons-material/Key';
interface GmailExportToolbarProps {
  toolsRoles: string[];
  setOpenAddGmailExport: Dispatch<SetStateAction<boolean>>;
  setOpenUpdateKey: Dispatch<SetStateAction<boolean>>;
  gmailExportKey: GmailExportKey | undefined;
  setOpenDeleteGmailExports: Dispatch<SetStateAction<boolean>>;
  gmailExportList: GmailAuditModel[];
  selectedExports: string[];
  apiUnavailable?: boolean;
}

export default function GmailExportToolbar({
  toolsRoles,
  setOpenAddGmailExport,
  setOpenUpdateKey,
  gmailExportKey,
  setOpenDeleteGmailExports,
  gmailExportList,
  selectedExports,
  apiUnavailable,
}: GmailExportToolbarProps) {
  const queryClient = useQueryClient();

  const models = gmailExportList.filter((model) =>
    selectedExports.includes(model.id)
  );
  const includesNotCompletedExports = models.some(
    (model) => model.status !== 'COMPLETED'
  );

  return (
    <SharedToolbar
      primaryButtons={
        <CreateButton
          variant="contained"
          disabled={
            !isAbleToSeeUIElement(
              toolsRoles,
              ToolsRoles.CreateGmailExports.value
            ) ||
            !gmailExportKey ||
            !!apiUnavailable
          }
          onClick={() => setOpenAddGmailExport(true)}
          label="Add Gmail Export"
          tooltipLabel={
            apiUnavailable
              ? 'Gmail Export is unavailable. The Email Audit API has been deprecated by Google.'
              : !gmailExportKey
              ? 'Must have a set encryption key.'
              : ''
          }
        />
      }
      secondaryButtons={
        <>
          <Tooltip
            arrow
            title={
              apiUnavailable
                ? 'Gmail Export is unavailable. The Email Audit API has been deprecated by Google.'
                : 'Update Key'
            }
          >
            <span>
              <IconButton
                disabled={!!apiUnavailable}
                onClick={() => {
                  setOpenUpdateKey(true);
                }}
              >
                <Key />
              </IconButton>
            </span>
          </Tooltip>
          <SharedIconButton
            variant="delete"
            onClick={() => {
              setOpenDeleteGmailExports(true);
            }}
            disabled={
              models.length === 0 ||
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.DeleteGmailExports.value
              ) ||
              includesNotCompletedExports
            }
            tooltipLabel={
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.DeleteGmailExports.value
              )
                ? `You are missing the ${ToolsRoles.DeleteGmailExports.name} permission to perform this action.`
                : includesNotCompletedExports
                ? 'You cannot delete exports that have not been completed or already deleted. Please unselect any with a status that is not completed.'
                : 'Delete'
            }
          />
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: ['GmailAuditModels'],
              });
              queryClient.invalidateQueries({
                queryKey: ['GmailExportKey'],
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="gmailExportsGridStates"
            module={ModuleList.modules.importExport.name}
            section={ModuleList.modules.importExport.sections.gmailExport}
          />
        </>
      }
    />
  );
}
